/**
 * useClaimRewards Hook
 *
 * Handles the complete claim flow:
 * 1. Fetch claim data from backend
 * 2. Submit claim transaction (on-chain or off-chain)
 * 3. Mark claim as completed in backend
 */

import { useState, useCallback, useEffect } from 'react';
import { claimsApi, ClaimData } from '@/api/claims';
import { useContractCalls } from './useContractCalls';

interface UseClaimRewardsReturn {
  claimData: ClaimData | null;
  isLoading: boolean;
  isClaiming: boolean;
  error: string | null;
  fetchClaimData: () => Promise<ClaimData | null>;
  submitClaim: () => Promise<{ success: boolean; txHash?: string }>;
}

export function useClaimRewards(challengeId: string): UseClaimRewardsReturn {
  const [claimData, setClaimData] = useState<ClaimData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { claimRewards } = useContractCalls();

  /**
   * Fetch claim data from backend
   */
  const fetchClaimData = useCallback(async (): Promise<ClaimData | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await claimsApi.getClaimData(challengeId);
      setClaimData(data);
      return data;
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to fetch claim data';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [challengeId]);

  /**
   * Submit claim - handles both on-chain and off-chain claims
   */
  const submitClaim = useCallback(async (): Promise<{ success: boolean; txHash?: string }> => {
    if (!claimData) {
      setError('No claim data available');
      return { success: false };
    }

    if (!claimData.canClaim) {
      setError('Cannot claim at this time');
      return { success: false };
    }

    setIsClaiming(true);
    setError(null);

    try {
      let txHash: string;

      if (claimData.isOffChain) {
        // ===== OFF-CHAIN CLAIM (Marathon) =====
        // Backend handles the transfer from admin wallet
        const result = await claimsApi.claimOffChain(challengeId);
        txHash = result.txHash;
      } else {
        // ===== ON-CHAIN CLAIM =====
        // Submit on-chain claim via smart contract
        const result = await claimRewards({
          challengeAddress: claimData.challengeAddress,
          proof: claimData.proof,
          index: claimData.index,
          amount: claimData.amount,
        });

        if (!result?.txnHash) {
          throw new Error('Transaction failed - no hash returned');
        }

        txHash = result.txnHash;

        // Mark as claimed in backend (off-chain claims are auto-marked)
        await claimsApi.markClaimed(challengeId, txHash);
      }

      // Refresh claim data
      await fetchClaimData();

      return { success: true, txHash };
    } catch (err: any) {
      const message = err.response?.data?.error || err.shortMessage || err.message || 'Claim failed';
      setError(message);
      return { success: false };
    } finally {
      setIsClaiming(false);
    }
  }, [challengeId, claimData, claimRewards, fetchClaimData]);

  // Fetch claim data on mount
  useEffect(() => {
    fetchClaimData();
  }, [fetchClaimData]);

  return {
    claimData,
    isLoading,
    isClaiming,
    error,
    fetchClaimData,
    submitClaim,
  };
}
